import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Clock, CheckCircle2, Plus, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { EmailSequencePanel } from '@/components/Email'
import type { BdrLeadDetailsProps } from './types'
import { faseInfo, panelSurface, insetSurface, bodyMuted, fieldControl } from './bdrLeadDetailsTheme'

type RightProps = Pick<
  BdrLeadDetailsProps,
  'selectedLead' | 'getCadencia' | 'handleAddPending' | 'togglePending' | 'removePending'
>

export function BdrLeadDetailsRightPanel({
  selectedLead,
  getCadencia,
  handleAddPending,
  togglePending,
  removePending,
}: RightProps) {
  const [rightTab, setRightTab] = useState<'timeline' | 'tasks' | 'email'>('timeline')
  const [pendingText, setPendingText] = useState('')
  const [pendingDate, setPendingDate] = useState('')

  const cadencia = getCadencia(selectedLead.id)

  function onAddPending() {
    if (!pendingText.trim()) return
    handleAddPending(pendingText, pendingDate || undefined)
    setPendingText('')
    setPendingDate('')
  }

  return (
      <div className="flex w-full min-w-0 shrink-0 flex-col gap-4 lg:min-h-[min(100%,32rem)] lg:max-h-[calc(94vh-12rem)]">
        {/* Tabs */}
        <div className={cn('flex items-center gap-1 p-1 rounded-xl border', insetSurface)}>
          {[
            { id: 'timeline' as const, label: 'Timeline' },
            { id: 'tasks' as const, label: 'Tasks', count: selectedLead.pendencias?.filter(p => !p.resolved).length },
            { id: 'email' as const, label: 'Email' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setRightTab(tab.id)}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold transition-all min-h-[44px]',
                rightTab === tab.id
                  ? 'bg-accent text-white shadow-lg shadow-accent/20'
                  : 'text-zinc-700 font-bold hover:text-zinc-900 hover:bg-zinc-200 dark:text-text-secondary dark:hover:text-text-primary dark:hover:bg-white/10'
              )}
            >
              {tab.label}
              {tab.count != null && tab.count > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-white/10 text-[10px] font-bold">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="min-h-[12rem] flex-1 overflow-y-auto custom-scrollbar lg:min-h-0">
          <AnimatePresence mode="wait">
            {rightTab === 'timeline' ? (
              <motion.div
                key="timeline"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
                className="space-y-0"
              >
                {cadencia?.historico && cadencia.historico.length > 0 ? (
                  <div className="relative">
                    {/* Connector line */}
                    <div className="absolute left-[15px] top-3 bottom-3 w-px bg-[color:var(--border)]" />

                    {cadencia.historico.map((step, i) => {
                      const stepPhase =
                        step.tentativa <= 5
                          ? faseInfo[1]
                          : step.tentativa <= 10
                            ? faseInfo[2]
                            : step.tentativa <= 12
                              ? faseInfo[3]
                              : faseInfo[4]
                      const stepColor = stepPhase.bar

                      return (
                        <div key={i} className="relative flex gap-4 py-3">
                          {/* Dot */}
                          <div
                            className="w-[30px] h-[30px] rounded-full flex items-center justify-center shrink-0 z-10 text-[10px] font-bold text-white"
                            style={{ backgroundColor: stepColor }}
                          >
                            {step.tentativa}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-zinc-900 dark:text-text-primary text-sm font-bold">
                                Tentativa #{step.tentativa}
                              </span>
                              <span className={cn(bodyMuted, 'text-xs font-semibold')}>
                                {step.canal}
                              </span>
                            </div>
                            <p className={cn(bodyMuted, 'text-xs mt-0.5 font-semibold')}>{step.resultado}</p>
                            {step.obs && (
                              <p className={cn(bodyMuted, 'text-xs mt-1')}>{step.obs}</p>
                            )}
                            <p className="text-zinc-600 dark:text-text-muted text-[10px] mt-1 font-medium">
                              {step.data}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-text-muted">
                    <Clock size={32} className="mb-3 opacity-30" />
                    <p className="text-sm">Nenhuma tentativa registrada</p>
                  </div>
                )}
              </motion.div>
            ) : rightTab === 'tasks' ? (
              <motion.div
                key="tasks"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                {/* Add Task */}
                <div className={cn(panelSurface, 'p-4 space-y-3')}>
                  <input
                    type="text"
                    value={pendingText}
                    onChange={e => setPendingText(e.target.value)}
                    placeholder="O que precisa ser feito?"
                    className={fieldControl}
                    onKeyDown={e => e.key === 'Enter' && onAddPending()}
                  />
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={pendingDate}
                      onChange={e => setPendingDate(e.target.value)}
                      className={cn(fieldControl, 'flex-1')}
                    />
                    <button
                      onClick={onAddPending}
                      disabled={!pendingText.trim()}
                      className={cn(
                        'px-4 rounded-lg flex items-center justify-center transition-all min-w-[44px] min-h-[44px] border',
                        pendingText.trim()
                          ? 'bg-accent text-white border-transparent hover:brightness-110'
                          : 'bg-[var(--bg-main)] text-text-muted border-[color:var(--border)] cursor-not-allowed',
                      )}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {/* Tasks List */}
                {selectedLead.pendencias && selectedLead.pendencias.length > 0 ? (
                  <div className="space-y-2">
                    {selectedLead.pendencias.map(p => (
                      <div
                        key={p.id}
                        className={cn(
                          panelSurface,
                          'p-3 flex items-start gap-3 transition-all',
                          p.resolved && 'opacity-60',
                        )}
                      >
                        <button
                          onClick={() => togglePending(p.id)}
                          className={cn(
                            'w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all min-w-[44px] min-h-[44px] -ml-3 -mt-3',
                            p.resolved
                              ? 'bg-accent border-accent'
                              : 'border-[color:var(--border)] hover:border-accent'
                          )}
                          style={{ minWidth: 20, minHeight: 20 }}
                        >
                          {p.resolved && <CheckCircle2 size={12} className="text-white" />}
                        </button>

                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            'text-sm',
                            p.resolved ? 'text-text-muted line-through' : 'text-text-primary'
                          )}>
                            {p.texto}
                          </p>
                          {p.dataRetorno && (
                            <p className="text-[10px] text-text-muted mt-1 flex items-center gap-1">
                              <Clock size={10} />
                              {p.dataRetorno}
                            </p>
                          )}
                        </div>

                        <button
                          onClick={() => removePending(p.id)}
                          className="p-1.5 rounded-lg hover:bg-red-500/10 text-text-muted hover:text-red-400 transition-colors shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-text-muted">
                    <CheckCircle2 size={32} className="mb-3 opacity-30" />
                    <p className="text-sm">Nenhuma pendência</p>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="email"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
              >
                <EmailSequencePanel
                  leadId={selectedLead.id}
                  leadEmail={selectedLead.email}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
  )
}
